/**
 * unsavedWork: "someone is halfway through a form on this page".
 *
 * meetcs2 (CS Shivangi Mishra, 11 Oct 2026): "karr is getting refresh in a
 * minute or two so if i am trying to add 5-6 agenda in a board meeting it is
 * getting refreshed and first page pe aa rh, again i will have to start from
 * first." The site deploys many times an hour on a busy day, and `swUpdate.ts`
 * used to reload every visible tab 3.5 s after it noticed one. A form that
 * holds its state in React lost all of it.
 *
 * So any form that would lose something says so here while it is open, and
 * `swUpdate.ts` asks before it reloads: with work on the page it shows "A new
 * version of Karr is ready" with a Reload button and waits for the person to
 * press it or move to another page.
 *
 * Dependency-light on purpose (React for the hook only), so `swUpdate.ts`
 * can read it without pulling a form into the first load.
 *
 * @since meetcs2 (17 Oct 2026)
 */
import { useEffect, useId } from 'react';

const holders = new Set<string>();

/** Mark (or clear) one form's unsaved work. `key` is the form's own, unique on the page. */
export function setUnsavedWork(key: string, dirty: boolean): void {
    if (dirty) holders.add(key);
    else holders.delete(key);
}

/** True while any form on this page holds work a reload would throw away. */
export function hasUnsavedWork(): boolean {
    return holders.size > 0;
}

/** For tests: forget every holder. */
export function resetUnsavedWork(): void {
    holders.clear();
}

/**
 * The form's side: pass true while it holds work a reload would lose. Cleared
 * when it turns false and when the form unmounts, so a closed form never holds
 * an update back.
 */
export function useUnsavedWork(dirty: boolean): void {
    const key = useId();
    useEffect(() => {
        setUnsavedWork(key, dirty);
        return () => setUnsavedWork(key, false);
    }, [key, dirty]);
}
